// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import { loginUser, logoutUser } from "../services/api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [logId, setLogId] = useState(null); // login session ID from backend
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Load stored auth on app start
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("user");
      const savedToken = localStorage.getItem("token");
      const savedLogId = localStorage.getItem("logId");

      if (savedUser && savedToken) {
        setUser(JSON.parse(savedUser));
        setToken(savedToken);
        if (savedLogId) setLogId(savedLogId);
      }
    } catch (error) {
      console.error("Error restoring auth state:", error);
      localStorage.clear();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ Login function
  const login = useCallback(async (username, password) => {
    setIsLoading(true);
    try {
      const result = await loginUser(username, password); // API call
      if (result?.success) {
        const { user, token, logId } = result;

        setUser(user);
        setToken(token);
        setLogId(logId);

        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("token", token);
        if (logId) localStorage.setItem("logId", logId);

        return { success: true, role: user.role };
      }
      return { success: false, message: result?.message || "Invalid login" };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, message: "Login failed. Try again." };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ Logout function
  const logout = useCallback(async () => {
    if (token && logId) {
      try {
        await logoutUser(logId, token); // API call
      } catch (error) {
        console.error("Logout API failed:", error);
      }
    }

    setUser(null);
    setToken(null);
    setLogId(null);
    localStorage.clear();
  }, [token, logId]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        logId,
        isLoading,
        login,
        logout,
        isAuthenticated: !!user, // ✅ helper flag
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
